import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, ActivityIndicator, Text, Platform, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppContext, AppContextType, Screen } from '../contexts/AppContext';
import { PhoneCallScreen } from '../components/PhoneCallScreen';
import { Notification } from '../components/Notification';
import { CALL_COMMAND_EVENT, TERMINAL_CALL_STATES, ACTIVE_CALL_STATES } from '../services/call-audio-bridge';
import { setCallMicrophoneMuted } from '../services/phone-dialer.service';
import { usePhoneDialer } from '../hooks/usePhoneDialer';
import { apiService } from '../services/api';
import { colors } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

/**
 * Standalone, pre-auth, query-string-driven call screen — the ONLY intended
 * consumer is a native WebView (see AppNavigator's own real call trigger on
 * native, and the App.tsx isCallRoute intercept that mounts this instead of
 * the normal AppNavigator router). Loaded as
 * https://connect.dislix.com/call?code=... — a single short-lived,
 * single-use code, NOT the real token/phoneNumber/contactId directly in the
 * URL. Confirmed real bug with the earlier direct-param design: this app's
 * own session JWT embeds the full business/subscription/permissions
 * payload and is large enough that putting it (plus the other params)
 * directly in the URL produced a URL long enough to make iOS's
 * SFSafariViewController fail with "site can't be reached", even though
 * the target server itself responded fine to a plain curl. The code is
 * exchanged for the real payload via GET /api/phone/call-session-code/:code
 * (sendifier_api) below, keeping the URL itself short regardless of how
 * large the JWT grows in the future.
 *
 * Exists because InnoCalls' dialer is a browser-only WebRTC SDK
 * (window.RTCPeerConnection, document script injection — see
 * phone-dialer.service.ts's own doc comment) that cannot run inside React
 * Native's JS environment at all; native calling instead opens this exact
 * URL in a WebView, giving the agent the SAME PhoneCallScreen experience
 * (call controls, contact quick actions, notes, channel icons) that already
 * works today on desktop web — not a stripped-down or reimplemented dialer.
 *
 * Security: the redeemed `token` is this app's own existing session JWT
 * (apiService.setAuthToken applies it once redeemed), so every subsequent
 * API call this page makes — including whatever the contact quick
 * actions/notes/channel-icon taps trigger — runs through the backend's
 * EXISTING per-user authorization checks (contact access scope, business
 * membership, etc.) exactly as it would for that same user on desktop web.
 * This page never bypasses or duplicates that authorization logic; it only
 * supplies the credential that makes the existing checks evaluate as that
 * specific logged-in agent, not a differently-scoped or unauthenticated
 * caller. The code itself is single-use and expires in 2 minutes (see
 * call-session-code.service.ts) — redeeming it twice, or after it expires,
 * fails exactly like any other invalid/expired credential.
 */
export const CallScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const [notification, setNotification] = useState<{
    visible: boolean;
    type: 'success' | 'error' | 'info' | 'warning' | 'message';
    title: string;
    message: string;
  }>({ visible: false, type: 'info', title: '', message: '' });

  const urlParams = useMemo(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return {};
    const search = new URLSearchParams(window.location.search);
    return {
      code: search.get('code') ?? undefined,
      phoneNumber: search.get('phoneNumber') ?? undefined,
      contactId: search.get('contactId') ?? undefined,
      contactName: search.get('contactName') ?? undefined,
    };
  }, []);

  const [params, setParams] = useState<{ phoneNumber?: string; contactId?: string; contactName?: string }>({});
  const [authReady, setAuthReady] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  useEffect(() => {
    // Two genuinely different ways to reach this screen already logged in:
    //   1. Native calling — no browser session of its own, a short-lived
    //      ?code= stands in for the real token (see this file's own top
    //      comment for why it can't just be the token directly).
    //   2. A normal, already-authenticated desktop/mobile WEB session —
    //      apiService's own constructor already restored whatever token was
    //      persisted in this browser's storage (the SAME singleton instance
    //      every other screen in the app uses), well before this component
    //      even mounted. In that case there's no ?code= at all — the call
    //      target instead comes as plain phoneNumber/contactId/contactName
    //      query params, matching how a normal in-app navigation would pass
    //      them, and this screen just needs to confirm a token is ALREADY
    //      present rather than trying to redeem anything.
    if (urlParams.code) {
      fetch(`${apiService.getBaseURL()}/api/phone/call-session-code/${encodeURIComponent(urlParams.code)}`)
        .then((res) => res.json())
        .then((body) => {
          if (!body?.data?.token) {
            setRedeemError('This call link has expired or was already used.');
            setAuthReady(true);
            return;
          }
          apiService.setAuthToken(body.data.token);
          // The call page's own refresh token (newer servers): it renews its sign-in during a
          // long call instead of failing with "token expired".
          if (body.data.refreshToken) apiService.setRefreshToken(body.data.refreshToken);
          setParams({ phoneNumber: body.data.phoneNumber, contactId: body.data.contactId, contactName: body.data.contactName });
          setAuthReady(true);
        })
        .catch((err) => {
          console.warn('[CallScreen] redeem fetch error:', err);
          setRedeemError('Failed to start the call. Please try again.');
          setAuthReady(true);
        });
      return;
    }

    if (apiService.getAuthToken()) {
      setParams({ phoneNumber: urlParams.phoneNumber, contactId: urlParams.contactId, contactName: urlParams.contactName });
      setAuthReady(true);
      return;
    }

    setRedeemError('You must be signed in to place a call.');
    setAuthReady(true);
  }, [urlParams.code, urlParams.phoneNumber, urlParams.contactId, urlParams.contactName]);

  // The name/avatar carried through the call-session code (or URL params)
  // is only ever a SNAPSHOT the caller happened to have on hand — passing
  // it through create-code -> mint -> redeem -> here proved fragile in
  // practice (an empty/stale name at the ORIGIN survives every hop intact,
  // since only undefined gets dropped by JSON, not ''). contactId, by
  // contrast, is reliably present and is the actual source of truth — so
  // once this page is authenticated (has a real token either via redeem or
  // an existing browser session), it fetches the CURRENT real contact
  // directly, the same way ContactsList.tsx/PhoneCallScreen's own contact
  // panel do, rather than trusting whatever string the caller passed in.
  const [resolvedContact, setResolvedContact] = useState<{ name?: string; avatar?: string } | null>(null);
  const [contactFetchDone, setContactFetchDone] = useState(false);
  useEffect(() => {
    if (!authReady) return;
    if (!params.contactId) {
      setContactFetchDone(true);
      return;
    }
    let cancelled = false;
    apiService.getParentContactById(params.contactId)
      .then((res) => {
        if (cancelled) return;
        const profile = res.data?.profile;
        setResolvedContact({ name: profile?.name, avatar: profile?.avatar });
      })
      .catch((err) => console.warn('[CallScreen] contact fetch error:', err))
      .finally(() => {
        if (!cancelled) setContactFetchDone(true);
      });
    return () => { cancelled = true; };
  }, [authReady, params.contactId]);

  // Confirmed real bug: usePhoneDialer's own init effect (which fetches
  // agent credentials via an authenticated request) used to fire
  // immediately on mount regardless of whether this page's own async code-
  // redemption had actually finished — losing that race meant isReady
  // stayed false forever, startCall() was never invoked, and
  // phoneCallContact stayed null the whole time (rendering as "Unknown"
  // despite the contact fetch above succeeding). Passing authReady through
  // lets usePhoneDialer wait for, and retry once, a real token exists.
  const phoneDialer = usePhoneDialer(authReady);
  const hangupRef = useRef(phoneDialer.hangup);
  hangupRef.current = phoneDialer.hangup;
  const nativeCancelled = useRef(false);
  useEffect(() => {
    const receive = async (event: Event) => {
      const command = (event as CustomEvent).detail;
      if (command?.action === 'hangup') {
        if (nativeCancelled.current) return;
        nativeCancelled.current = true;
        try { await hangupRef.current(); }
        finally { (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: 'dismissCallScreen' })); }
      } else if (command?.action === 'mute' && typeof command.muted === 'boolean') setCallMicrophoneMuted(command.muted);
    };
    window.addEventListener(CALL_COMMAND_EVENT, receive);
    return () => { window.removeEventListener(CALL_COMMAND_EVENT, receive); setCallMicrophoneMuted(false); };
  }, []);
  useEffect(() => {
    if (ACTIVE_CALL_STATES.has(phoneDialer.callState) || TERMINAL_CALL_STATES.has(phoneDialer.callState)) {
      (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: 'callState', state: phoneDialer.callState }));
    }
  }, [phoneDialer.callState]);

  // Places the call exactly once, as soon as the dialer reports it's ready
  // AND the contact fetch above has settled (success or failure) — guarded
  // so React's dev-mode double-invoke (or a re-render before isReady flips)
  // can never fire startCall twice for the same page load.
  const hasStartedRef = useRef(false);
  useEffect(() => {
    if (nativeCancelled.current || !authReady || !contactFetchDone || !phoneDialer.isReady || hasStartedRef.current) return;
    if (!params.phoneNumber) return;
    hasStartedRef.current = true;
    // Prefers the freshly-fetched real name/avatar; falls back to whatever
    // the caller passed through the session code (still useful if the
    // fetch failed or the contact has since been deleted), and finally to
    // undefined (usePhoneDialer's own phoneNumber-as-name fallback).
    const name = resolvedContact?.name || params.contactName;
    const avatar = resolvedContact?.avatar;
    phoneDialer.startCall(params.phoneNumber, params.contactId, name ? { name, avatar } : undefined);
  }, [authReady, contactFetchDone, phoneDialer.isReady, params.phoneNumber, params.contactId, params.contactName, resolvedContact]);

  const showNotification = (type: 'success' | 'error' | 'info' | 'warning' | 'message', title: string, message: string) => {
    setNotification({ visible: true, type, title, message });
  };

  // Only meaningful when this page is actually loaded inside the native
  // app's in-app WebView modal (AppNavigator.tsx's startNativePhoneCall) —
  // window.ReactNativeWebView is the standard react-native-webview bridge
  // object, present ONLY in that context, never in a plain browser tab
  // (desktop web, or someone opening this URL directly). Posts a message
  // the native side listens for (see AppNavigator.tsx's WebView onMessage)
  // to close the modal and return to the app — this page has no other way
  // back, since it's a fully standalone route with nothing to navigate to.
  const canDismissToApp = Platform.OS === 'web' && typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;
  const dismissToApp = () => {
    (window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: 'dismissCallScreen' }));
  };

  // Every navigation-related field PhoneCallScreen's own type surface
  // requires (via useAppContext/useConversationHandler) but never actually
  // invokes in practice on this page — there's nowhere to navigate TO; this
  // page has no other screen to show. Real, working values are supplied
  // only for the phone-call fields (backed by this page's own usePhoneDialer
  // instance) and showNotification (backed by a local Notification banner
  // below), since those are the only context values PhoneCallScreen and
  // useConversationHandler genuinely use.
  const contextValue: AppContextType = {
    currentScreen: 'call' as Screen,
    navigateTo: () => {},
    showNotification,
    showClickableNotification: (type, title, message) => showNotification(type, title, message),
    handleGetStarted: () => {},
    handleSignUp: () => {},
    handleBackToLogin: () => {},
    handleForgotPassword: () => {},
    handleBackToWelcome: () => {},
    handleBackToRegister: () => {},
    handleLogout: () => {},
    handleWhatsAppTest: () => {},
    handleChatNavigation: () => {},
    handleConversationNavigation: () => {},
    joinConversationRooms: async () => {},
    handleSocialLogin: () => {},
    pendingEmail: '',
    setPendingEmail: () => {},
    isNewUser: false,
    setIsNewUser: () => {},
    activeTab: 'chat',
    setActiveTab: () => {},
    routeParams: {},
    phoneCallState: phoneDialer.callState,
    phoneCallDuration: phoneDialer.duration,
    phoneCallError: phoneDialer.error,
    phoneCallContact: phoneDialer.contact,
    phoneCallMinimized: phoneDialer.minimized,
    setPhoneCallMinimized: phoneDialer.setMinimized,
    isPhoneDialerReady: phoneDialer.isReady,
    startPhoneCall: phoneDialer.startCall,
    hangupPhoneCall: phoneDialer.hangup,
    // Confirmed real bug: PhoneCallScreen's own "Dismiss" button (shown
    // once a call reaches a terminal state) only ever reset this page's
    // LOCAL call state (phoneDialer.dismissCall alone) — it never closed
    // the surrounding native WebView modal, so the agent was left staring
    // at an empty/idle call screen still open inside the in-app browser
    // with no way back except the separate X button. Wrapped here (only
    // for THIS page's context — the normal in-app desktop/mobile-web usage
    // of PhoneCallScreen keeps plain phoneDialer.dismissCall via its own
    // AppNavigator.tsx wiring, since there's no WebView container to close
    // there) so "Dismiss" behaves exactly like the X button: reset state,
    // then tell the native side to close the modal.
    dismissPhoneCall: () => {
      phoneDialer.dismissCall();
      dismissToApp();
    },
  };

  const dismissButton = canDismissToApp ? (
    <TouchableOpacity style={styles.dismissButton} onPress={dismissToApp} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
      <Ionicons name="close" size={22} color={colors.white as string} />
    </TouchableOpacity>
  ) : null;

  // Confirmed real bug: this used to only wait for authReady/phoneNumber,
  // then render PhoneCallScreen immediately — but phoneDialer.contact isn't
  // actually set until startCall() fires (gated on the dialer AND the
  // contact fetch both settling, some real async work later). PhoneCallScreen
  // has no loading state of its own for a null contact — it just renders
  // "Unknown" until the real value arrives, which briefly but visibly
  // flashed on every single call before correcting itself. Waiting for
  // hasStartedRef to have actually fired (the same guard startCall's own
  // effect uses) means the fully-populated screen appears directly, with no
  // "Unknown" ever visible at all.
  if (!authReady || redeemError || !params.phoneNumber || !hasStartedRef.current) {
    return (
      <View style={styles.centered}>
        {dismissButton}
        {redeemError || (authReady && !params.phoneNumber) ? (
          <Text style={styles.errorText}>{redeemError || 'Missing call parameters.'}</Text>
        ) : (
          <ActivityIndicator size="large" color={colors.primary as string} />
        )}
      </View>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      <View style={styles.container}>
        {dismissButton}
        <PhoneCallScreen />
        <Notification
          visible={notification.visible}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification((n) => ({ ...n, visible: false }))}
        />
      </View>
    </AppContext.Provider>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.primary as string },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.background.primary as string, padding: 24 },
  errorText: { color: colors.text.secondary as string, fontSize: 14, textAlign: 'center' },
  dismissButton: {
    position: 'absolute',
    top: 12,
    left: 12,
    zIndex: 1000,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0,0,0,0.5)',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
